/**
 * VolatilityChart — asset-class volatilities, always the FQM surface.
 *
 * Asset-class vol is a market-level series, not a per-mandate one, so this
 * chart reads the login-only `/api/mars/live/asset-class-vol` endpoint —
 * the same FQM series the Forecasts page's Volatility tab renders. It is
 * NOT the fund-gated portfolio timeseries: fulcrum PMs (dar-nn only) and
 * client orgs must still see it. Trace assembly is shared with the
 * portal-chat inline vol chart via ``lib/plotly/assetClassVolChart``.
 */
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { Layout } from 'plotly.js';
import PlotlyChart from '@/lib/plotly/PlotlyChart';
import ChartCard from '@/components/ChartCard';
import QueryState from '@/components/QueryState';
import { ASSET_COLORS } from '@/lib/plotly/colors';
import { mergeLayout } from '@/lib/plotly/defaults';
import { buildAssetVolTraces, type VolAsset } from '@/lib/plotly/assetClassVolChart';
import { getWindowRange, tickConfigForTimeframe } from '@/lib/utils/timeframe';
import { COMPACT_DATE_TICKS, compactChartLayout } from '../lib/compactChart';
import { fetchVolatility, mlKeys } from '../api';
import { vintageCursorAnnotation, vintageCursorShape } from '../lib/vintageCursor';

import type { UiAsset } from '../types';
import { ASSET_DISPLAY } from '../types';
import type { Timeframe } from '../store';

export interface VolatilityChartProps {
  selectedAssets: ReadonlySet<UiAsset>;
  vintageDate: string;
  timeframe: Timeframe;
}

export default function VolatilityChart({
  selectedAssets,
  vintageDate,
  timeframe,
}: VolatilityChartProps): JSX.Element {
  const { data: volData, isLoading } = useQuery({
    queryKey: mlKeys.volatility(),
    queryFn: fetchVolatility,
    staleTime: 5 * 60 * 1000,
  });

  const { traces, layout } = useMemo(() => {
    if (!volData?.dates?.length || !volData?.series) {
      return { traces: [], layout: {} as Partial<Layout> };
    }
    const [startStr, endStr] = getWindowRange(vintageDate, timeframe);

    // The vol payload (acVol VariableNames from the .mat) is keyed by the
    // canonical asset name ('Equities', 'Bonds', 'Commodities',
    // 'Inflation'), never the display labels ('Fixed Income',
    // 'Inflation Swaps') — same convention as the exposures payload.
    const assets: VolAsset[] = [...selectedAssets].map((a) => ({
      seriesKey: a,
      label: ASSET_DISPLAY[a],
      color: ASSET_COLORS[a],
    }));

    const out = buildAssetVolTraces(volData, {
      assets,
      startDate: startStr,
      endDate: endStr,
      lineWidth: 2,
    });

    // Fit the y-axis to the visible window. Floor stays at 0 (vol is
    // non-negative, and autorange drifts into negative bands when a series
    // has missing values at the head); the top tracks the data — commodity
    // vol spikes past 70% ann., far above any fixed ceiling.
    let yMax = 0;
    for (const t of out) {
      for (const v of (t as { y?: number[] }).y ?? []) {
        if (v > yMax) yMax = v;
      }
    }
    const yRange: [number, number] = [0, yMax > 0 ? yMax * 1.1 : 30];

    const endPadded = new Date(vintageDate);
    endPadded.setMonth(endPadded.getMonth() + 1);
    const { tickformat } = tickConfigForTimeframe(timeframe);
    const cursorShape = vintageCursorShape(vintageDate);
    const cursorAnnotation = vintageCursorAnnotation(vintageDate);

    const preset = compactChartLayout({ title: 'Asset Class Volatilities' });
    const lo: Partial<Layout> = mergeLayout({
      ...preset,
      xaxis: {
        ...preset.xaxis,
        type: 'date',
        range: [startStr, endPadded.toISOString().slice(0, 10)],
        tickformat,
        ...COMPACT_DATE_TICKS,
        showgrid: true,
        gridcolor: '#E0E0E0',
      },
      yaxis: {
        ...preset.yaxis,
        title: { text: '% Ann.', standoff: 10 },
        tickformat: '.0f',
        showgrid: true,
        gridcolor: '#E0E0E0',
        range: yRange,
      },
      shapes: cursorShape ? [cursorShape] : [],
      annotations: cursorAnnotation ? [cursorAnnotation] : [],
      hovermode: 'x unified',
    });

    return { traces: out, layout: lo };
  }, [volData, selectedAssets, vintageDate, timeframe]);

  return (
    <ChartCard height={300}>
      <QueryState
        query={{ isLoading, isError: false, data: traces }}
        isEmpty={(t) => t.length === 0}
        loadingLabel="Loading"
        emptyLabel="No volatility data"
      >
        {() => <PlotlyChart data={traces} layout={layout} />}
      </QueryState>
    </ChartCard>
  );
}
